import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';
import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Alert,
    useTheme,
    styled,
    Tooltip,
    IconButton,
    Collapse,
    Card,
    CardContent,
    Divider,
    Skeleton,
    useMediaQuery
} from '@mui/material';
import {
    CalendarToday as CalendarIcon,
    Class as ClassIcon,
    AccessTime as TimeIcon,
    Person as PersonIcon,
    HowToReg as HowToRegIcon,
    Info as InfoIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    Refresh as RefreshIcon,
    FilterList as FilterListIcon,
    Print as PrintIcon,
    GetApp as DownloadIcon
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO, isValid, addDays } from 'date-fns';
import { tr } from 'date-fns/locale';
import { OgrenciDurum, SinifResponseModal, YoklamaRaporuResponseModal } from '../../../types/modals';
import { YoklamaRaporuRequestModal } from '../../../types/RequestModals/YoklamaRaporuRequestModal';
import { getSiniflar, getYoklamaDetay, getYoklamaRaporu } from '../../../services/api/yoklamaSistemi';
import { useAuth } from '../../../contexts/AuthContext';
import { getCurrentSezon, getSezonOptions } from '../../../utils/sezon';

// Styled components (önceki tanımlarınız aynen kalabilir)
const StyledFormControl = styled(FormControl)(({ theme }) => ({
    minWidth: 200,
    marginBottom: theme.spacing(3),
    '& .MuiOutlinedInput-root': {
        borderRadius: theme.shape.borderRadius * 1.5,
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
        },
    },
}));

// Diğer styled component tanımları...

// Durum açıklamaları
const DURUM_ACIKLAMALAR: Record<string, string> = { 
    'Devamsız': 'Devamsız',
    'Geç': 'Geç',
    'İzinli': 'İzinli',
    'Etüt': 'Etüt',
    '': 'Geldi'
};

// Günler
const GUNLER = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

// Helper components
const LoadingPlaceholder = () => {
    return (
        <Box sx={{ mb: 4 }}>
            <Skeleton variant="rectangular" height={60} sx={{ mb: 2, borderRadius: 2 }} />
            <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
        </Box>
    );
};

const EmptyState = ({ message }: { message: string }) => {
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                py: 5,
                px: 2,
                textAlign: 'center'
            }}
        >
            <InfoIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
                {message}
            </Typography>
        </Box>
    );
};

const YoklamaRaporu: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [selectedSinif, setSelectedSinif] = useState<number | ''>('');
    const [selectedDate, setSelectedDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
    const [expandedDers, setExpandedDers] = useState<number | null>(null);
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const [selectedDers, setSelectedDers] = useState<number | null>(null);
    const [filterYoklamaAlindi, setFilterYoklamaAlindi] = useState<string>('all');
    const [sezon, setSezon] = useState<number>(getCurrentSezon());
    const sezonOptions = getSezonOptions();

    const handleSezonChange = useCallback((event: SelectChangeEvent) => {
        setSezon(Number(event.target.value));
        setSelectedSinif('');
        setExpandedDers(null);
    }, []);

    // Kurum kodu bilgisini kullanıcı bilgilerinden al
    const kurumKodu = localStorage.getItem('kurumKodu') ? localStorage.getItem('kurumKodu')!.toString() : "";

    // Sınıf listesini getir
    const {
        data: siniflar = [],
        isLoading: isSiniflarLoading,
        isError: isSiniflarError,
        error: siniflarError
    } = useQuery<SinifResponseModal[], Error>({
        queryKey: ['siniflar', kurumKodu, sezon],
        queryFn: () => getSiniflar(kurumKodu, sezon),
        enabled: !!kurumKodu,
        staleTime: 30 * 60 * 1000, // 30 dakika
    });

    // Yoklama raporunu getir
    const {
        data: dersProgrami = [],
        isLoading: isDersProgramiLoading,
        isError: isDersProgramiError,
        error: dersProgramiError,
        refetch: refetchDersProgrami
    } = useQuery<YoklamaRaporuResponseModal[], Error>({
        queryKey: ['yoklamaRaporu', kurumKodu, selectedSinif, selectedDate, sezon],
        queryFn: async () => {
            if (!selectedSinif) return [];

            const request: YoklamaRaporuRequestModal = {
                kurumKodu,
                sinifId: selectedSinif as number,
                tarih: selectedDate,
                sezon
            };

            return getYoklamaRaporu(request);
        },
        enabled: selectedSinif !== '' && !!kurumKodu,
        staleTime: 5 * 60 * 1000, // 5 dakika
    });

    // Yoklama detayını getir
    const {
        data: yoklamaDetay,
        isLoading: isYoklamaDetayLoading,
        isError: isYoklamaDetayError,
        error: yoklamaDetayError,
        refetch: refetchYoklamaDetay
    } = useQuery<OgrenciDurum[], Error>({
        queryKey: ['yoklamaDetay', kurumKodu, selectedDers, selectedDate, sezon],
        queryFn: async () => {
            if (!selectedDers) throw new Error('Ders seçilmedi');

            return getYoklamaDetay(kurumKodu, selectedDers, selectedDate, sezon);
        },
        enabled: selectedDers !== null && !!kurumKodu,
        staleTime: 5 * 60 * 1000, // 5 dakika
    });

    // Memoized values
    const filteredDersProgrami = useMemo(() => {
        if (!dersProgrami) return [];

        if (filterYoklamaAlindi === 'all') return dersProgrami;
        if (filterYoklamaAlindi === 'taken') return dersProgrami.filter(d => d.yoklamaAlindi);
        if (filterYoklamaAlindi === 'notTaken') return dersProgrami.filter(d => !d.yoklamaAlindi);

        return dersProgrami;
    }, [dersProgrami, filterYoklamaAlindi]);

    const dersProgramiStats = useMemo(() => {
        if (!dersProgrami || dersProgrami.length === 0) {
            return {
                total: 0,
                taken: 0,
                notTaken: 0,
                percentage: 0
            };
        }

        const taken = dersProgrami.filter(d => d.yoklamaAlindi).length;
        const total = dersProgrami.length;

        return {
            total,
            taken,
            notTaken: total - taken,
            percentage: Math.round((taken / total) * 100)
        };
    }, [dersProgrami]);

    const ogretmenStats = useMemo(() => {
        if (!dersProgrami || dersProgrami.length === 0) return [];

        const ogretmenMap = new Map<string, { total: number, taken: number }>();

        dersProgrami.forEach(ders => {
            const current = ogretmenMap.get(ders.ogretmenAdi) || { total: 0, taken: 0 };
            ogretmenMap.set(ders.ogretmenAdi, {
                total: current.total + 1,
                taken: current.taken + (ders.yoklamaAlindi ? 1 : 0)
            });
        });

        return Array.from(ogretmenMap.entries()).map(([name, stats]) => ({
            name,
            ...stats,
            percentage: Math.round((stats.taken / stats.total) * 100)
        }));
    }, [dersProgrami]);

    // Helper functions
    const getStudentStatusColor = useCallback((status: string) => {
        switch (status) {
            case 'G':
            case '': // Boş durum "Geldi" anlamına geliyor
                return theme.palette.success.main;
            case 'I':
            case 'izinli':
                return theme.palette.error.main;
            case 'R':
            case 'raporlu':
                return theme.palette.warning.main;
            case 'E':
            case 'erkenCikis':
                return theme.palette.info.main;
            case 'Y':
            case 'yarimGun':
                return theme.palette.secondary.main;
            case 'devamsiz':
                return theme.palette.error.main;
            case 'gec':
                return theme.palette.warning.main;
            case 'etut':
                return theme.palette.info.main;
            default:
                return theme.palette.grey[500];
        }
    }, [theme]);

    const durumIstatistikleri = useMemo(() => {
        if (!yoklamaDetay || !yoklamaDetay.length) return [];

        const durumlar = yoklamaDetay.reduce((acc, ogrenci) => {
            const durum = ogrenci.durum;
            acc[durum] = (acc[durum] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        return Object.entries(durumlar)
            .filter(([_, count]) => count > 0)
            .map(([durum, count]) => ({
                durum,
                durumAciklama: DURUM_ACIKLAMALAR[durum] || 'Bilinmiyor',
                count,
                color: getStudentStatusColor(durum)
            }));
    }, [yoklamaDetay, getStudentStatusColor]);

    // Callbacks
    const handleSinifChange = useCallback((event: SelectChangeEvent) => {
        setSelectedSinif(event.target.value as unknown as number);
        setExpandedDers(null);
    }, []);

    const handleDateChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedDate(event.target.value);
        setExpandedDers(null);
    }, []);

    const handleDersClick = useCallback((id: number) => {
        if (expandedDers === id) {
            setExpandedDers(null);
            setSelectedDers(null);
        } else {
            setExpandedDers(id);
            setSelectedDers(id);
        }
    }, [expandedDers]);

    const handleDialogOpen = useCallback((id: number) => {
        setSelectedDers(id);
        setDialogOpen(true);
    }, []);

    const handleDialogClose = useCallback(() => {
        setDialogOpen(false);
    }, []);

    const handleRefresh = useCallback(() => {
        if (selectedSinif !== '') {
            refetchDersProgrami();
            if (selectedDers !== null) {
                refetchYoklamaDetay();
            }
        }
    }, [selectedSinif, selectedDers, refetchDersProgrami, refetchYoklamaDetay]);

    const handleFilterChange = useCallback((filter: string) => {
        setFilterYoklamaAlindi(filter);
    }, []);

    const handlePrint = useCallback(() => {
        window.print();
    }, []);

    const handleExport = useCallback(() => {
        // Implement export functionality (e.g., to Excel or PDF)
        alert('Bu özellik henüz uygulanmadı. Yakında eklenecek.');
    }, []);

    // Get status chip based on yoklama status
    const getStatusChip = useCallback((ders: YoklamaRaporuResponseModal) => {
        if (!ders.yoklamaAlindi) {
            return (
                <Chip
                    label="Yoklama Alınmadı"
                    size="small"
                    icon={<CancelIcon fontSize="small" />}
                    sx={{
                        backgroundColor: theme.palette.error.light,
                        color: theme.palette.error.dark,
                        fontWeight: 500
                    }}
                />
            );
        }

        if (ders.yoklamaOrani && ders.yoklamaOrani < 80) {
            return (
                <Chip
                    label={`Kısmi (${ders.yoklamaOrani}%)`}
                    size="small"
                    icon={<InfoIcon fontSize="small" />}
                    sx={{
                        backgroundColor: theme.palette.warning.light,
                        color: theme.palette.warning.dark,
                        fontWeight: 500
                    }}
                />
            );
        }

        return (
            <Chip
                label="Yoklama Alındı"
                size="small"
                icon={<CheckCircleIcon fontSize="small" />}
                sx={{
                    backgroundColor: theme.palette.success.light,
                    color: theme.palette.success.dark,
                    fontWeight: 500
                }}
            />
        );
    }, [theme]);

    const formatDate = useCallback((dateString: string) => {
        try {
            const date = parseISO(dateString);
            if (isValid(date)) {
                return format(date, 'dd MMMM yyyy, HH:mm', { locale: tr });
            }
            return 'Geçersiz tarih';
        } catch (error) {
            return 'Geçersiz tarih';
        }
    }, []);

    const getDayName = useCallback((dateString: string) => {
        try {
            const date = parseISO(dateString);
            if (isValid(date)) {
                return GUNLER[date.getDay()];
            }
            return '';
        } catch (error) {
            return '';
        }
    }, []);

    return (
        <Box sx={{
            maxWidth: '100%',
            overflowX: 'hidden',
            '@media print': {
                margin: 0,
                padding: 0
            }
        }}>
            <Typography
                variant="h5"
                component="h1"
                gutterBottom
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 3,
                    '@media print': { display: 'none' }
                }}
            >
                <HowToRegIcon color="primary" />
                Yoklama Raporu
            </Typography>

            {/* Filters Section */}
            <Box
                sx={{
                    p: 2,
                    borderRadius: theme.shape.borderRadius * 1.5,
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: theme.shadows[1],
                    mb: 3,
                    '@media print': { display: 'none' }
                }}
            >
                <Grid container spacing={3} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <StyledFormControl fullWidth>
                            <InputLabel id="sezon-select-label">Sezon</InputLabel>
                            <Select
                                labelId="sezon-select-label"
                                id="sezon-select"
                                value={sezon.toString()}
                                onChange={handleSezonChange}
                                label="Sezon"
                            >
                                {sezonOptions.map((year) => (
                                    <MenuItem key={year} value={year}>
                                        {year - 1} - {year} Eğitim Yılı
                                    </MenuItem>
                                ))}
                            </Select>
                        </StyledFormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <StyledFormControl fullWidth>
                            <InputLabel id="sinif-select-label">Sınıf Seçin</InputLabel>
                            <Select
                                labelId="sinif-select-label"
                                id="sinif-select"
                                value={selectedSinif.toString()}
                                onChange={handleSinifChange}
                                label="Sınıf Seçin"
                                disabled={isSiniflarLoading}
                            >
                                <MenuItem value="">
                                    <em>Seçiniz</em>
                                </MenuItem>
                                {siniflar.map((sinif) => (
                                    <MenuItem key={sinif.id} value={sinif.id}>
                                        {sinif.adi}
                                    </MenuItem>
                                ))}
                            </Select>
                        </StyledFormControl>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <StyledFormControl fullWidth>
                            <InputLabel htmlFor="date-input" shrink>
                                Tarih
                            </InputLabel>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: theme.shape.borderRadius * 1.5,
                                p: 1,
                                mt: 2
                            }}>
                                <CalendarIcon color="primary" sx={{ mr: 1 }} />
                                <input
                                    type="date"
                                    id="date-input"
                                    value={selectedDate}
                                    onChange={handleDateChange}
                                    style={{
                                        border: 'none',
                                        outline: 'none',
                                        width: '100%',
                                        fontFamily: theme.typography.fontFamily,
                                        fontSize: '1rem'
                                    }}
                                />
                            </Box>
                        </StyledFormControl>
                    </Grid>
                    <Grid item xs={12} sm={3} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<RefreshIcon />}
                            onClick={handleRefresh}
                            disabled={selectedSinif === '' || isDersProgramiLoading}
                            sx={{
                                borderRadius: theme.shape.borderRadius * 1.5,
                                height: '56px'
                            }}
                        >
                            Yenile
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<PrintIcon />}
                            onClick={handlePrint}
                            disabled={!dersProgrami.length}
                            sx={{
                                borderRadius: theme.shape.borderRadius * 1.5,
                                height: '56px'
                            }}
                        >
                            Yazdır
                        </Button>
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={<DownloadIcon />}
                            onClick={handleExport}
                            disabled={!dersProgrami.length}
                            sx={{
                                borderRadius: theme.shape.borderRadius * 1.5,
                                height: '56px',
                                display: { xs: 'none', md: 'flex' }
                            }}
                        >
                            Dışa Aktar
                        </Button>
                    </Grid>
                </Grid>

                {selectedSinif !== '' && dersProgrami.length > 0 && (
                    <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Typography variant="subtitle2" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                            <FilterListIcon fontSize="small" sx={{ mr: 0.5 }} />
                            Filtrele:
                        </Typography>
                        <Chip
                            label="Tümü"
                            onClick={() => handleFilterChange('all')}
                            color={filterYoklamaAlindi === 'all' ? 'primary' : 'default'}
                            variant={filterYoklamaAlindi === 'all' ? 'filled' : 'outlined'}
                        />
                        <Chip
                            label="Yoklama Alınanlar"
                            onClick={() => handleFilterChange('taken')}
                            color={filterYoklamaAlindi === 'taken' ? 'success' : 'default'}
                            variant={filterYoklamaAlindi === 'taken' ? 'filled' : 'outlined'}
                        />
                        <Chip
                            label="Yoklama Alınmayanlar"
                            onClick={() => handleFilterChange('notTaken')}
                            color={filterYoklamaAlindi === 'notTaken' ? 'error' : 'default'}
                            variant={filterYoklamaAlindi === 'notTaken' ? 'filled' : 'outlined'}
                        />
                    </Box>
                )}
            </Box>

            {/* Content Section */}
            {isSiniflarLoading ? (
                <LoadingPlaceholder />
            ) : isSiniflarError ? (
                <Alert severity="error" sx={{ my: 2 }}>
                    {siniflarError?.message || 'Sınıf listesi yüklenirken bir hata oluştu. Lütfen tekrar deneyin.'}
                </Alert>
            ) : isDersProgramiLoading ? (
                <LoadingPlaceholder />
            ) : isDersProgramiError ? (
                <Alert severity="error" sx={{ my: 2 }}>
                    {dersProgramiError?.message || 'Ders programı yüklenirken bir hata oluştu. Lütfen tekrar deneyin.'}
                </Alert>
            ) : selectedSinif === '' ? (
                <EmptyState message="Lütfen ders programını görüntülemek için bir sınıf seçin." />
            ) : filteredDersProgrami.length === 0 ? (
                <Alert severity="warning" sx={{ my: 2 }}>
                    {filterYoklamaAlindi !== 'all'
                        ? `Seçilen filtre kriterlerine uygun ders bulunamadı.`
                        : `Seçilen sınıf ve tarih için ders programı bulunamadı.`}
                </Alert>
            ) : (
                <>
                    {/* Header with date info */}
                    <Box
                        sx={{
                            mb: 2,
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            justifyContent: 'space-between',
                            gap: 1
                        }}
                    >
                        <Typography variant="h6" component="h2">
                            {siniflar.find(s => s.id === selectedSinif)?.adi} Sınıfı - {formatDate(selectedDate)} {getDayName(selectedDate)}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            <Chip
                                icon={<CheckCircleIcon />}
                                label={`Alınan: ${dersProgramiStats.taken}`}
                                size="small"
                                color="success"
                                variant="outlined"
                            />
                            <Chip
                                icon={<CancelIcon />}
                                label={`Alınmayan: ${dersProgramiStats.notTaken}`}
                                size="small"
                                color="error"
                                variant="outlined"
                            />
                            <Chip
                                icon={<InfoIcon />}
                                label={`Oran: %${dersProgramiStats.percentage}`}
                                size="small"
                                color={dersProgramiStats.percentage >= 80 ? "success" : "warning"}
                                variant="outlined"
                            />
                        </Box>
                    </Box>

                    {/* Ders Programı Table */}
                    <TableContainer
                        component={Paper}
                        sx={{
                            borderRadius: theme.shape.borderRadius * 1.5,
                            boxShadow: theme.shadows[2],
                            mb: 4,
                            overflow: 'hidden'
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell
                                        sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            color: theme.palette.common.white,
                                            fontWeight: 600
                                        }}
                                    >
                                        Saat
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            color: theme.palette.common.white,
                                            fontWeight: 600
                                        }}
                                    >
                                        Ders
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            color: theme.palette.common.white,
                                            fontWeight: 600
                                        }}
                                    >
                                        Öğretmen
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            color: theme.palette.common.white,
                                            fontWeight: 600
                                        }}
                                    >
                                        Yoklama Durumu
                                    </TableCell>
                                    <TableCell
                                        align="center"
                                        sx={{
                                            backgroundColor: theme.palette.primary.main,
                                            color: theme.palette.common.white,
                                            fontWeight: 600
                                        }}
                                    >
                                        Detay
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredDersProgrami.map((ders) => (
                                    <React.Fragment key={ders.id}>
                                        <TableRow
                                            sx={{
                                                '&:nth-of-type(odd)': {
                                                    backgroundColor: theme.palette.action.hover,
                                                },
                                                '&:hover': {
                                                    backgroundColor: theme.palette.action.selected,
                                                },
                                                transition: 'background-color 0.2s ease',
                                            }}
                                        >
                                            <TableCell>{ders.saat}</TableCell>
                                            <TableCell>{ders.dersAdi}</TableCell>
                                            <TableCell>{ders.ogretmenAdi}</TableCell>
                                            <TableCell align="center">
                                                {getStatusChip(ders)}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title={ders.yoklamaAlindi ? "Yoklama detayını görüntüle" : "Yoklama alınmadı"}>
                                                    <span> {/* Wrapper to handle disabled tooltip */}
                                                        <IconButton
                                                            color="primary"
                                                            onClick={() => handleDersClick(ders.id)}
                                                            disabled={!ders.yoklamaAlindi}
                                                            size="small"
                                                        >
                                                            {expandedDers === ders.id ?
                                                                <ExpandLessIcon /> :
                                                                <ExpandMoreIcon />
                                                            }
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{ p: 0 }}>
                                                <Collapse in={expandedDers === ders.id} timeout="auto" unmountOnExit>
                                                    <Box sx={{ p: 3, backgroundColor: theme.palette.grey[50] }}>
                                                        {isYoklamaDetayLoading ? (
                                                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                                                <CircularProgress size={30} />
                                                            </Box>
                                                        ) : isYoklamaDetayError ? (
                                                            <Alert severity="error" sx={{ my: 1 }}>
                                                                {yoklamaDetayError?.message || 'Yoklama detayı yüklenirken bir hata oluştu.'}
                                                            </Alert>
                                                        ) : yoklamaDetay && yoklamaDetay.length > 0 ? (
                                                            <>
                                                                <Typography
                                                                    variant="subtitle1"
                                                                    gutterBottom
                                                                    sx={{
                                                                        fontWeight: 600,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 1
                                                                    }}
                                                                >
                                                                    <ClassIcon color="primary" fontSize="small" />
                                                                    {ders.dersAdi} - Yoklama Detayı
                                                                </Typography>

                                                                <Grid container spacing={2} sx={{ mb: 2 }}>
                                                                    <Grid item xs={12} sm={4}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <PersonIcon color="primary" fontSize="small" />
                                                                            <Typography variant="body2">
                                                                                Öğretmen: <strong>{ders.ogretmenAdi}</strong>
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={4}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <TimeIcon color="primary" fontSize="small" />
                                                                            <Typography variant="body2">
                                                                                Saat: <strong>{ders.saat}</strong>
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                    <Grid item xs={12} sm={4}>
                                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                            <ClassIcon color="primary" fontSize="small" />
                                                                            <Typography variant="body2">
                                                                                Sınıf: <strong>{ders.sinifAdi}</strong>
                                                                            </Typography>
                                                                        </Box>
                                                                    </Grid>
                                                                </Grid>

                                                                <Divider sx={{ my: 2 }} />

                                                                <Grid container spacing={1} sx={{ mb: 1 }}>
                                                                    <Grid item xs={6}>
                                                                        <Typography variant="subtitle2" color="text.secondary">
                                                                            Öğrenci
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid item xs={3}>
                                                                        <Typography variant="subtitle2" color="text.secondary">
                                                                            Öğrenci No
                                                                        </Typography>
                                                                    </Grid>
                                                                    <Grid item xs={3}>
                                                                        <Typography variant="subtitle2" color="text.secondary" align="center">
                                                                            Durum
                                                                        </Typography>
                                                                    </Grid>
                                                                </Grid>

                                                                <Paper
                                                                    variant="outlined"
                                                                    sx={{
                                                                        maxHeight: 300,
                                                                        overflow: 'auto',
                                                                        borderRadius: theme.shape.borderRadius * 1.5
                                                                    }}
                                                                >
                                                                    {yoklamaDetay.map((ogrenci) => (
                                                                        <Box
                                                                            key={ogrenci.ogrenciId}
                                                                            sx={{
                                                                                display: 'flex',
                                                                                justifyContent: 'space-between',
                                                                                alignItems: 'center',
                                                                                p: 1.5,
                                                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                                                                '&:last-child': {
                                                                                    borderBottom: 'none',
                                                                                },
                                                                                '&:hover': {
                                                                                    backgroundColor: theme.palette.action.hover,
                                                                                },
                                                                            }}
                                                                        >
                                                                            <Grid container alignItems="center" spacing={1}>
                                                                                <Grid item xs={6}>
                                                                                    <Typography variant="body2">{ogrenci.adSoyad}</Typography>
                                                                                </Grid>
                                                                                <Grid item xs={3}>
                                                                                    <Typography variant="body2" color="text.secondary">
                                                                                        {ogrenci.ogrenciNo}
                                                                                    </Typography>
                                                                                </Grid>
                                                                                <Grid item xs={3} sx={{ display: 'flex', justifyContent: 'center' }}>
                                                                                    <Chip
                                                                                        label={DURUM_ACIKLAMALAR[ogrenci.durum] || 'Bilinmiyor'}
                                                                                        size="small"
                                                                                        sx={{
                                                                                            backgroundColor: `${getStudentStatusColor(ogrenci.durum)}20`,
                                                                                            color: getStudentStatusColor(ogrenci.durum),
                                                                                            fontWeight: 500,
                                                                                            minWidth: 80
                                                                                        }}
                                                                                    />
                                                                                </Grid>
                                                                            </Grid>
                                                                        </Box>
                                                                    ))}
                                                                </Paper>

                                                                <Box
                                                                    sx={{
                                                                        mt: 2,
                                                                        display: 'flex',
                                                                        flexDirection: { xs: 'column', sm: 'row' },
                                                                        justifyContent: 'space-between',
                                                                        alignItems: { xs: 'flex-start', sm: 'center' },
                                                                        gap: 2
                                                                    }}
                                                                >
                                                                    <Box>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            Toplam Öğrenci: {yoklamaDetay.length}
                                                                        </Typography>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            Gelmeyen Öğrenci: {yoklamaDetay.filter(o => o.durum !== '' && o.durum !== 'G').length}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                                        {durumIstatistikleri.map(({ durum, durumAciklama, count, color }) => (
                                                                            <Chip
                                                                                key={durum}
                                                                                label={`${durumAciklama}: ${count}`}
                                                                                size="small"
                                                                                sx={{
                                                                                    backgroundColor: `${color}20`,
                                                                                    color: color,
                                                                                    fontWeight: 500
                                                                                }}
                                                                            />
                                                                        ))}
                                                                    </Box>
                                                                </Box>
                                                            </>
                                                        ) : (
                                                            <Typography color="text.secondary" align="center">
                                                                Yoklama detayı bulunamadı.
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Özet Kartları */}
                    <Grid container spacing={3} sx={{ mt: 2 }}>
                        {/* Ders Özeti Kartı */}
                        <Grid item xs={12} sm={4}>
                            <Card sx={{ borderRadius: theme.shape.borderRadius * 1.5, boxShadow: theme.shadows[2] }}>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}
                                    >
                                        <ClassIcon color="primary" />
                                        Ders Özeti
                                    </Typography>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                        <Typography variant="body2">Toplam Ders:</Typography>
                                        <Typography variant="body2" fontWeight="bold">{dersProgramiStats.total}</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                        <Typography variant="body2">Yoklama Alınan:</Typography>
                                        <Typography variant="body2" fontWeight="bold" color="success.main">
                                            {dersProgramiStats.taken}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                        <Typography variant="body2">Yoklama Alınmayan:</Typography>
                                        <Typography variant="body2" fontWeight="bold" color="error.main">
                                            {dersProgramiStats.notTaken}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Öğretmen Özeti Kartı */}
                        <Grid item xs={12} sm={4}>
                            <Card sx={{ borderRadius: theme.shape.borderRadius * 1.5, boxShadow: theme.shadows[2] }}>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}
                                    >
                                        <PersonIcon color="primary" />
                                        Öğretmen Özeti
                                    </Typography>
                                    <Divider sx={{ my: 1 }} />
                                    <Box sx={{ mt: 2 }}>
                                        {ogretmenStats.map(({ name, total, taken, percentage }) => (
                                            <Box key={name} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                                    {name}:
                                                </Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight="bold"
                                                        color={percentage === 100 ? "success.main" : percentage >= 70 ? "warning.main" : "error.main"}
                                                    >
                                                        {taken}/{total}
                                                    </Typography>
                                                    <Chip
                                                        label={`${percentage}%`}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.7rem',
                                                            backgroundColor: percentage === 100
                                                                ? theme.palette.success.light
                                                                : percentage >= 70
                                                                    ? theme.palette.warning.light
                                                                    : theme.palette.error.light,
                                                            color: percentage === 100
                                                                ? theme.palette.success.dark
                                                                : percentage >= 70
                                                                    ? theme.palette.warning.dark
                                                                    : theme.palette.error.dark,
                                                        }}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Yoklama Oranı Kartı */}
                        <Grid item xs={12} sm={4}>
                            <Card sx={{ borderRadius: theme.shape.borderRadius * 1.5, boxShadow: theme.shadows[2] }}>
                                <CardContent>
                                    <Typography
                                        variant="h6"
                                        gutterBottom
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}
                                    >
                                        <HowToRegIcon color="primary" />
                                        Yoklama Oranı
                                    </Typography>
                                    <Divider sx={{ my: 1 }} />
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            mt: 2,
                                            position: 'relative'
                                        }}
                                    >
                                        <CircularProgress
                                            variant="determinate"
                                            value={dersProgramiStats.percentage}
                                            size={80}
                                            thickness={5}
                                            sx={{
                                                color: dersProgramiStats.percentage >= 90
                                                    ? theme.palette.success.main
                                                    : dersProgramiStats.percentage >= 70
                                                        ? theme.palette.warning.main
                                                        : theme.palette.error.main
                                            }}
                                        />
                                        <Typography
                                            variant="h5"
                                            component="div"
                                            sx={{
                                                position: 'absolute',
                                                fontWeight: 'bold',
                                                color: dersProgramiStats.percentage >= 90
                                                    ? theme.palette.success.main
                                                    : dersProgramiStats.percentage >= 70
                                                        ? theme.palette.warning.main
                                                        : theme.palette.error.main
                                            }}
                                        >
                                            {dersProgramiStats.percentage}%
                                        </Typography>
                                    </Box>
                                    <Typography
                                        variant="body2"
                                        align="center"
                                        sx={{ mt: 2 }}
                                    >
                                        {dersProgramiStats.percentage >= 90 ? (
                                            <Box
                                                sx={{
                                                    color: 'success.main',
                                                    fontWeight: 'medium',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 0.5
                                                }}
                                            >
                                                <CheckCircleIcon fontSize="small" />
                                                Yoklama oranı çok iyi
                                            </Box>
                                        ) : dersProgramiStats.percentage >= 70 ? (
                                            <Box
                                                sx={{
                                                    color: 'warning.main',
                                                    fontWeight: 'medium',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 0.5
                                                }}
                                            >
                                                <InfoIcon fontSize="small" />
                                                Yoklama oranı orta
                                            </Box>
                                        ) : (
                                            <Box
                                                sx={{
                                                    color: 'error.main',
                                                    fontWeight: 'medium',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 0.5
                                                }}
                                            >
                                                <CancelIcon fontSize="small" />
                                                Yoklama oranı düşük
                                            </Box>
                                        )}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </>
            )}

            {/* Yoklama Detay Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleDialogClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: theme.shape.borderRadius * 1.5,
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        bgcolor: theme.palette.primary.main,
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}
                >
                    <HowToRegIcon />
                    Yoklama Detayı
                </DialogTitle>
                <DialogContent dividers>
                    {isYoklamaDetayLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : isYoklamaDetayError ? (
                        <Alert severity="error" sx={{ my: 1 }}>
                            {yoklamaDetayError?.message || 'Yoklama detayı yüklenirken bir hata oluştu.'}
                        </Alert>
                    ) : yoklamaDetay && yoklamaDetay.length > 0 ? (
                        <Box>
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2" color="text.secondary">Ders</Typography>
                                    <Typography variant="body1">
                                        {dersProgrami.find(d => d.id === selectedDers)?.dersAdi || ''}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2" color="text.secondary">Öğretmen</Typography>
                                    <Typography variant="body1">
                                        {dersProgrami.find(d => d.id === selectedDers)?.ogretmenAdi || ''}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Typography variant="subtitle2" color="text.secondary">Saat</Typography>
                                    <Typography variant="body1">
                                        {dersProgrami.find(d => d.id === selectedDers)?.saat || ''}
                                    </Typography>
                                </Grid>
                            </Grid>

                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                                Öğrenci Yoklama Listesi
                            </Typography>

                            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Öğrenci No</TableCell>
                                            <TableCell>Ad Soyad</TableCell>
                                            <TableCell align="center">Durum</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {yoklamaDetay.map((ogrenci) => (
                                            <TableRow key={ogrenci.ogrenciId}>
                                                <TableCell>{ogrenci.ogrenciNo}</TableCell>
                                                <TableCell>{ogrenci.adSoyad}</TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={DURUM_ACIKLAMALAR[ogrenci.durum] || 'Bilinmiyor'}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: `${getStudentStatusColor(ogrenci.durum)}20`,
                                                            color: getStudentStatusColor(ogrenci.durum),
                                                            fontWeight: 500
                                                        }}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Typography variant="subtitle2" gutterBottom>
                                Özet
                            </Typography>

                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {durumIstatistikleri.map(({ durum, durumAciklama, count, color }) => (
                                    <Chip
                                        key={durum}
                                        label={`${durumAciklama}: ${count}`}
                                        size="small"
                                        sx={{
                                            backgroundColor: `${color}20`,
                                            color: color,
                                            fontWeight: 500
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    ) : (
                        <Typography color="text.secondary" align="center">
                            Yoklama detayı bulunamadı.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleDialogClose}
                        color="primary"
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        sx={{ borderRadius: theme.shape.borderRadius * 1.5 }}
                    >
                        Kapat
                    </Button>
                    <Button
                        onClick={handlePrint}
                        color="secondary"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        sx={{ borderRadius: theme.shape.borderRadius * 1.5 }}
                    >
                        Yazdır
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default YoklamaRaporu;